import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { MarketplaceGrid } from '@features/connections/generic/components/MarketplaceGrid';
import { CreateProductFlow } from '@features/products/components/create/CreateProductFlow';
import { useTutorialStore } from '../../../../application/store/TutorialStore';
import { APP_CONFIG } from '../../../../config/app.config';
import { useBottomSheet, BottomSheetSize } from '@shared/shell/bottomsheet/useBottomSheet';
import OnboardingOverlayCard from '../../shared/OnboardingOverlayCard';
import OnboardingSkipFooter from '../../shared/OnboardingSkipFooter';

interface ProductCatalogEmptyViewProps {
  onDismiss: () => void;
}

const ProductCatalogEmptyView: React.FC<ProductCatalogEmptyViewProps> = ({ onDismiss }) => {
  const colors = APP_CONFIG.ui.theme.colors;
  const queryClient = useQueryClient();
  const markStepCompleted = useTutorialStore((s) => s.markStepCompleted);
  const { openBottomSheet, closeBottomSheet } = useBottomSheet();

  return (
    <OnboardingOverlayCard
      title="Your product catalog is still empty"
      footer={<OnboardingSkipFooter onDismiss={onDismiss} />}
    >
      <Typography variant="body2">
        Your product catalog is still empty. Connect your store and we&apos;ll import your products
        automatically.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <MarketplaceGrid
          onNewConnection={() => {
            markStepCompleted('marketplace-connection');
            queryClient.invalidateQueries({ queryKey: ['connections'] });
          }}
        />

        <Box
          sx={{
            border: 1,
            borderColor: colors.border,
            borderRadius: '8px',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2">Add products manually</Typography>
            <Typography variant="body2" color="text.secondary">
              You can also create products manually
            </Typography>
          </Box>
          <Button
            variant="text"
            onClick={() =>
              openBottomSheet({
                title: 'Create Product',
                component: <CreateProductFlow onClose={closeBottomSheet} />,
                size: BottomSheetSize.MD,
              })
            }
          >
            Create products
          </Button>
        </Box>
      </Box>
    </OnboardingOverlayCard>
  );
};

export default ProductCatalogEmptyView;
